import { Fragment, ReactNode } from 'react';
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react';
import { CheckIcon, ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

import { cn } from 'support/helpers/cn/cn';
import { Divider } from 'components/Divider/Divider';

export type StepStatus = 'completed' | 'current' | 'upcoming' | 'disabled';

export type StepItem = {
  title: ReactNode;
  description?: ReactNode;
  status?: StepStatus;
  children: ReactNode;
};

type StepsProps = {
  steps: Array<StepItem>;
  // Static instruction mode: every step stays expanded (no collapsing, chevron or card chrome) and
  // steps are separated by dividers. Use for read-once instructions that have no progress/status.
  forceOpen?: boolean;
};

export const Steps = ({ steps, forceOpen = false }: StepsProps) => {
  if (forceOpen) {
    return (
      <div className="flex flex-col gap-6">
        {steps.map((step, index) => (
          <Fragment key={`${index}|${steps.length}`}>
            {index > 0 && <Divider />}
            <StaticStep step={step} index={index + 1} />
          </Fragment>
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {steps.map((step, index) => (
        <CollapsibleStep step={step} index={index + 1} key={`${index}|${steps.length}|${step.status}`} />
      ))}
    </div>
  );
};

const StaticStep = ({ step, index }: { step: StepItem; index: number }) => (
  <div className="flex items-start gap-4">
    <StepCircle status={step.status} index={index} />
    <div className="min-w-0 flex-1 space-y-2 text-sm leading-5 text-gray-900">
      <p className="font-semibold">{step.title}</p>
      {step.description ? <p className="text-gray-500">{step.description}</p> : null}
      {step.children}
    </div>
  </div>
);

const CollapsibleStep = ({ step, index }: { step: StepItem; index: number }) => {
  const isDisabled = step.status === 'disabled';

  return (
    <Disclosure defaultOpen={step.status === 'current'}>
      {({ open }) => (
        <>
          <DisclosureButton
            disabled={isDisabled}
            className={cn(
              'flex w-full items-center gap-4 rounded-md bg-white p-3 text-start',
              isDisabled && 'opacity-50',
            )}
          >
            <StepCircle status={step.status} index={index} />
            <div className="flex min-w-0 flex-1 flex-col items-start">
              <span className="text-sm font-semibold text-gray-700">{step.title}</span>
              {step.description ? <span className="text-sm text-gray-500">{step.description}</span> : null}
            </div>
            {open ? (
              <ChevronDownIcon className="size-6 shrink-0 text-gray-400" />
            ) : (
              <ChevronRightIcon className="size-6 shrink-0 text-gray-400" />
            )}
          </DisclosureButton>
          <DisclosurePanel className="pl-17">{step.children}</DisclosurePanel>
        </>
      )}
    </Disclosure>
  );
};

const StepCircle = ({ status, index }: { status?: StepStatus; index: number }) => {
  if (status === 'completed') {
    return (
      <span className="flex size-10 shrink-0 items-center justify-center rounded-full bg-green-100" aria-hidden="true">
        <CheckIcon className="size-6 text-green-600" />
      </span>
    );
  }

  const isFilled = status === 'current' || status === undefined;

  return (
    <span
      className={cn(
        'flex size-10 shrink-0 items-center justify-center rounded-full text-sm',
        isFilled ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-500',
      )}
      aria-hidden="true"
    >
      {index}
    </span>
  );
};
